import { Theme } from '@material-ui/core';
import MuiButton from '@material-ui/core/Button';
import ListItemText from '@material-ui/core/ListItemText';
import { useTheme } from '@material-ui/core/styles';
import MuiToggleButton from '@material-ui/lab/ToggleButton';
import cx from 'classnames';
import React, { FC, useEffect, useState } from 'react';

import DownArrow from '../../images/arrow-down';
import Checkmark from '../../images/checkmark-rounded.svg';
import StyledMenu from './StyledMenu';
import StyledMenuItem from './StyledMenuItem';
import useStyles from './styles';

const VARIANT = {
  default: 'default',
  dropdown: 'dropdown',
};

const getArrow = (theme: Theme, btnOn: boolean, useClass: string) => {
  if (btnOn) {
    return <DownArrow color={theme.palette.common.blue500} useClass={useClass} />;
  }
  return <DownArrow color={theme.palette.common.gray600} useClass={useClass} />;
};

export interface ToggleButtonProps {
  className?: string;
  closeOnSelect?: boolean;
  defaultSelection: string;
  label?: string;
  menuList?: string[];
  onSelect?: (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => void;
  onToggleOff?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onToggleOn?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  variant?: 'default' | 'dropdown';
  selected?: boolean;
}

export const ToggleButton: FC<ToggleButtonProps> = ({
  variant,
  menuList,
  defaultSelection,
  label = 'Toggle',
  onSelect,
  onToggleOn,
  onToggleOff,
  closeOnSelect,
  className,
  selected = false,
}) => {
  const [active, setActive] = useState(selected);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedId, setSelected] = useState(-1);
  const [text, setText] = useState(label);

  useEffect(() => {
    if (defaultSelection && menuList) {
      const idx = menuList.indexOf(defaultSelection);
      setSelected(idx);

      if (idx !== -1) {
        setText(defaultSelection);
        setActive(true);
      }
    }
  }, [defaultSelection, menuList]);

  const theme = useTheme();
  const styles = useStyles(active);

  const handleToggle = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setActive(!active);

    if (active && onToggleOff) {
      onToggleOff(e);
    } else if (!active && onToggleOn) {
      onToggleOn(e);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelection = (e: React.MouseEvent<HTMLLIElement, MouseEvent>, id: number) => {
    if (selectedId !== id) {
      setSelected(id);

      if (id !== -1 && menuList) {
        setText(menuList[id]);
        setActive(true);
      } else {
        setText(label);
        setActive(false);
      }

      if (onSelect) onSelect(e);
      if (closeOnSelect) handleClose();
    }
  };

  const Arrow = getArrow(theme, active, styles.arrow);

  if (variant === VARIANT.dropdown) {
    return (
      <>
        <MuiButton className={cx(className, styles.btn)} onClick={handleClick}>
          {text}
          {Arrow}
        </MuiButton>
        <StyledMenu
          anchorEl={anchorEl}
          className={styles.menu}
          keepMounted
          onClose={handleClose}
          open={Boolean(anchorEl)}
        >
          {menuList &&
            menuList.map((item, id) => (
              <StyledMenuItem key={item} onClick={e => handleSelection(e, id)} selected={selectedId === id}>
                <ListItemText className={styles.text}>{item}</ListItemText>
                {selectedId === id && (
                  <div className={styles.checkmark}>
                    <img alt="checkmark" className={styles.checkmark} src={Checkmark} />
                  </div>
                )}
              </StyledMenuItem>
            ))}
        </StyledMenu>
      </>
    );
  }

  return (
    <MuiToggleButton
      className={cx(className, styles.btn, styles.pill)}
      onClick={e => handleToggle(e)}
      selected={selected}
      value={label}
    >
      {label}
    </MuiToggleButton>
  );
};

export default ToggleButton;
